import React, { useEffect } from "react";
import { useGlobalContent } from "../GlobalContent";
import "./PhoneModal.css";

// "Call us" pop-up opened from the navbar. Text, phone numbers and email come from
// the admin (Website → Navbar, footer & offices → “Call us” pop-up).
const telHref = (phone) => `tel:${String(phone).replace(/[^0-9+]/g, "")}`;

const PhoneModal = ({ isOpen, onClose }) => {
  const { g, en, isFR } = useGlobalContent();
  const m = g.callModal || {};
  // Phone numbers and the email are entered once (English side) for both languages
  const enPhones = en.callModal?.phones || [];
  const phones = (m.phones || [])
    .map((p, i) => ({ label: p.label, number: p.phone || enPhones[i]?.phone }))
    .filter((p) => p.number);
  const mailto = en.callModal?.link || m.link || "";
  const email = mailto.replace(/^mailto:/, "");

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="pm__backdrop" onClick={onClose} />

      <div className="pm__dialog" role="dialog" aria-labelledby="pm-title" aria-modal="true">
        <button className="pm__close" onClick={onClose} aria-label={isFR ? "Fermer" : "Close"}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="pm__header">
          <div className="pm__header-logo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            <span>{m.brand}</span>
          </div>
          {m.tag && <span className="pm__header-tag">{m.tag}</span>}
        </div>

        <div className="pm__body">
          <h2 id="pm-title" className="pm__title">{m.title}</h2>
          {m.subtitle && <p className="pm__subtitle">{m.subtitle}</p>}

          {/* Primary CTAs */}
          {phones.map((p) => (
            <a key={p.number} href={telHref(p.number)} className="pm__cta-primary">
              <div className="pm__cta-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="pm__cta-text">
                <span className="pm__cta-label">{p.label}</span>
                <span className="pm__cta-number">{p.number}</span>
              </div>
              <svg className="pm__cta-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m0 0L9 4m4 4L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          ))}

          {/* Secondary options */}
          <div className="pm__options">
            {email && (
              <a href={mailto} className="pm__option">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M2 7l10 7 10-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="pm__option-text">
                  <span className="pm__option-label">{m.emailLabel}</span>
                  <span className="pm__option-value">{email}</span>
                </div>
              </a>
            )}

            <div className="pm__option-divider" />

            <a href={isFR ? "/fr/contact" : "/contact"} className="pm__option" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="pm__option-text">
                <span className="pm__option-label">{m.quoteLabel}</span>
                <span className="pm__option-value">{m.quoteValue}</span>
              </div>
            </a>
          </div>

          {/* Footer info */}
          <div className="pm__footer-info">
            {m.hours && (
              <div className="pm__info-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span style={{ whiteSpace: "pre" }}>{m.hours}</span>
              </div>
            )}
            {m.emergency && (
              <div className="pm__info-item pm__info-item--accent">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                </svg>
                <span>{m.emergency}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PhoneModal;
